import React, { useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { MapPin, Calendar, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import dayjs from 'dayjs';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icon for activity markers
const createActivityIcon = (type, index) => {
  const colors = {
    'Check-In': '#22c55e',
    'Check-Out': '#ef4444',
    'Break-In': '#f59e0b',
    'Break-Out': '#3b82f6',
    default: '#6b7280',
  };

  const icons = {
    'Check-In': '▶',
    'Check-Out': '◼',
    'Break-In': '☕',
    'Break-Out': '→',
    default: '•',
  };

  const color = colors[type] || colors.default;
  const icon = icons[type] || icons.default;

  return L.divIcon({
    className: 'custom-activity-marker',
    html: `
      <div style="
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
      ">
        <div style="
          background-color: ${color};
          color: white;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        ">
          ${index !== undefined ? index + 1 : icon}
        </div>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

// Component to fit bounds
const FitBounds = ({ trail }) => {
  const map = useMap();

  React.useEffect(() => {
    if (trail && trail.length > 0) {
      const validPoints = trail.filter(p => p.latitude && p.longitude);
      if (validPoints.length > 0) {
        const bounds = L.latLngBounds(
          validPoints.map(p => [p.latitude, p.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [trail, map]);

  return null;
};

/**
 * Location Trail Map Widget - Shows user's location history
 */
const LocationTrailMap = ({
  trail,
  loading,
  selectedDate,
  onDateChange,
  userName
}) => {
  const [selectedPoint, setSelectedPoint] = useState(null);

  // Filter valid trail points
  const validTrail = useMemo(() => {
    return trail?.filter(p => p.latitude && p.longitude) || [];
  }, [trail]);

  // Create polyline coordinates
  const polylinePositions = useMemo(() => {
    return validTrail.map(p => [p.latitude, p.longitude]);
  }, [validTrail]);

  // Default center
  const mapCenter = validTrail.length > 0
    ? [validTrail[0].latitude, validTrail[0].longitude]
    : [20.5937, 78.9629];

  // Activity points (filtered)
  const activityPoints = useMemo(() => {
    return validTrail.filter(p => p.activity_type);
  }, [validTrail]);

  // Timeline data
  const timeline = useMemo(() => {
    return activityPoints.map((point, index) => ({
      ...point,
      index,
      time: point.timestamp ? dayjs(point.timestamp).format('hh:mm A') : 'N/A',
    }));
  }, [activityPoints]);

  const handlePrevDay = () => {
    const newDate = dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD');
    onDateChange?.(newDate);
  };

  const handleNextDay = () => {
    const newDate = dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD');
    if (dayjs(newDate).isAfter(dayjs())) return;
    onDateChange?.(newDate);
  };

  const isToday = dayjs(selectedDate).isSame(dayjs(), 'day');

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin size={18} className="text-indigo-600" />
            <div>
              <h3 className="text-base font-semibold text-gray-800">Location Trail</h3>
              {userName && (
                <p className="text-sm text-gray-1000">{userName}</p>
              )}
            </div>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center gap-1">
            <button
              onClick={handlePrevDay}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronLeft size={16} className="text-gray-600" />
            </button>
            <div className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded">
              <Calendar size={14} className="text-gray-1000" />
              <span className="text-sm font-medium text-gray-700">
                {dayjs(selectedDate).format('MMM D, YYYY')}
              </span>
            </div>
            <button
              onClick={handleNextDay}
              disabled={isToday}
              className={`p-1.5 rounded transition-colors ${
                isToday ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
              }`}
            >
              <ChevronRight size={16} className="text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="h-40">
        {validTrail.length > 0 ? (
          <MapContainer
            center={mapCenter}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds trail={validTrail} />

            {/* Trail line */}
            {polylinePositions.length > 1 && (
              <Polyline
                positions={polylinePositions}
                pathOptions={{
                  color: '#6366f1',
                  weight: 3,
                  opacity: 0.7,
                  dashArray: '5, 10',
                }}
              />
            )}

            {/* Activity markers */}
            {activityPoints.map((point, index) => (
              <Marker
                key={point.tracking_id || index}
                position={[point.latitude, point.longitude]}
                icon={createActivityIcon(point.activity_type, index)}
                eventHandlers={{
                  click: () => setSelectedPoint(point),
                }}
              >
                <Popup>
                  <div className="p-2 min-w-[140px]">
                    <p className="font-semibold text-gray-800">
                      {point.activity_type || 'Location Point'}
                    </p>
                    <p className="text-sm text-gray-1000">
                      {point.timestamp ? dayjs(point.timestamp).format('hh:mm A') : 'N/A'}
                    </p>
                    {point.location_name && (
                      <p className="text-xs text-gray-400 mt-1">
                        {point.location_name}
                      </p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        ) : (
          <div className="h-full flex items-center justify-center bg-gray-50">
            <div className="text-center text-gray-1000">
              <MapPin size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No location data for this date</p>
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      {timeline.length > 0 && (
        <div className="p-4 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1.5">
            <Clock size={14} />
            Timeline
          </p>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {timeline.map((item, index) => (
              <div
                key={item.tracking_id || index}
                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                  selectedPoint?.tracking_id === item.tracking_id
                    ? 'bg-indigo-50 border border-indigo-200'
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => setSelectedPoint(item)}
              >
                <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-medium">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">
                    {item.activity_type || 'Location Update'}
                  </p>
                  {item.location_name && (
                    <p className="text-xs text-gray-400">{item.location_name}</p>
                  )}
                </div>
                <span className="text-xs text-gray-1000">{item.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="px-4 pb-4">
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-1000"></span>
            Check-In
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-1000"></span>
            Check-Out
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-yellow-1000"></span>
            Break Start
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-blue-1000"></span>
            Break End
          </span>
        </div>
      </div>
    </div>
  );
};

export default LocationTrailMap;
